import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Page } from '@app/app/shared/models/page';
import { Result } from '@app/app/shared/store';
import { SelectOption } from '@app/app/shared/styleguide/components/selects/sg-select/sg-select.component';
import { LucideSearch } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { USER_GROUPS_DATA_SERVICE, UserGroupsDataService } from '../../../../../page-modules/admin/services/groups/groups-data.service';
import {
  AndRequest,
  BooleanFilter,
  BooleanQueryRequest,
  BooleanQueryType,
  GroupsRequest,
  NotRequest,
  OrRequest,
} from '../../../../models/admin/boolean-filters.model';
import { UserGroups } from '../../../../models/admin/group/user-groups.model';

const DEFAULT_GROUP_OPERATOR = 'OR';

@Component({
  selector: 'ptl-v3-group-filter',
  templateUrl: './v3-group-filter.component.html',
  styleUrls: ['./v3-group-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3GroupFilterComponent implements OnInit, OnDestroy {
  @Input() selectedFilters: BooleanFilter[];
  @Input() playlistUid: string;
  @Input() playlistUids: string[] = [];
  @Output() filterDeleted = new EventEmitter<void>();
  @Output() filterSaved = new EventEmitter<BooleanFilter>();

  activeGroups: UserGroups[] = [];
  foundGroups: UserGroups[] = [];
  groupIs = true;
  logicalOperator: 'OR' | 'AND' = DEFAULT_GROUP_OPERATOR;
  loadingGroups = false;
  loadingMoreGroups = false;
  canLoadMore = false;
  searchTerm = '';
  DEFAULT_PAGE_SIZE = 25;

  isOptions: SelectOption<boolean>[] = [];
  operatorOptions: SelectOption<'OR' | 'AND'>[] = [];

  protected readonly LucideSearch = LucideSearch;

  private page = 0;
  private searchInputSubject$ = new Subject<void>();
  private searchInputSubscription: Subscription;

  constructor(
    @Inject(USER_GROUPS_DATA_SERVICE) private userGroupsService: UserGroupsDataService,
    private translocoService: TranslocoService,
    private cdr: ChangeDetectorRef,
  ) {
    this.searchInputSubscription = this.searchInputSubject$.pipe(debounceTime(500)).subscribe(() => this.fireSearch());
  }

  ngOnInit(): void {
    if (this.playlistUid != null) {
      this.playlistUids = [this.playlistUid];
    }
    this.isOptions = [
      { value: true, label: this.translocoService.translate('translations.reviews.filter.is') },
      { value: false, label: this.translocoService.translate('translations.reviews.filter.isNot') },
    ];
    this.operatorOptions = [
      { value: 'AND', label: this.translocoService.translate('translations.logicalOperators.and') },
      { value: 'OR', label: this.translocoService.translate('translations.logicalOperators.or') },
    ];
    this.getSettingsFromFilters();
    this.fireSearch();
  }

  ngOnDestroy(): void {
    this.searchInputSubscription?.unsubscribe();
  }

  onGroupIsChange(groupIs: boolean): void {
    if (groupIs) {
      this.groupIs = true;
    } else {
      this.groupIs = false;
      this.logicalOperator = DEFAULT_GROUP_OPERATOR;
    }
  }

  onLogicalOperatorSelection(operator: 'OR' | 'AND'): void {
    this.logicalOperator = operator;
    this.groupIs = true;
  }

  deleteFilter(): void {
    this.filterDeleted.emit();
  }

  applyFilter(): void {
    const request = this.getRequest(this.activeGroups.map((group) => group._id));
    const message = this.getMessage(this.activeGroups.map((group) => group.title));
    this.filterSaved.emit({ request: request, message: message, type: 'GROUP', activeUserGroups: this.activeGroups });
  }

  onSearchInputChange(term: string): void {
    this.searchTerm = term;
    this.loadingGroups = true;
    this.searchInputSubject$.next();
  }

  toggleGroup(group: UserGroups): void {
    if (this.isGroupSelected(group)) {
      this.activeGroups = this.activeGroups.filter((item) => item._id !== group._id);
    } else {
      this.activeGroups = this.activeGroups.concat(group);
    }
  }

  // Selected groups the current search doesn't return; listed first so a search never hides a ticked group.
  getSelectedGroupsOutsideResults(): UserGroups[] {
    return this.activeGroups.filter((group) => !this.foundGroups.some((found) => found._id === group._id));
  }

  onResultsScroll(event: Event): void {
    const list = event.target as HTMLElement;
    const distanceToBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
    if (distanceToBottom < 40) {
      this.loadNextPageOfGroups();
    }
  }

  private loadNextPageOfGroups(): void {
    if (this.loadingGroups || this.loadingMoreGroups || !this.canLoadMore) {
      return;
    }
    this.page++;
    this.loadingMoreGroups = true;
    const requestedTerm = this.searchTerm;
    this.searchGroups((result) => {
      this.loadingMoreGroups = false;
      // The user typed a new search while this page was loading; its results belong to the old term.
      if (requestedTerm !== this.searchTerm) {
        return;
      }
      if (result.isSuccess) {
        this.addFoundGroups(result.value.content);
        this.canLoadMore = this.foundGroups.length < result.value.totalNumberOfElement;
      }
      this.cdr.detectChanges();
    });
  }

  isGroupSelected(group: UserGroups): boolean {
    return this.activeGroups.some((item) => item._id === group._id);
  }

  private getRequest(groupIds: string[]): BooleanQueryRequest {
    if (this.logicalOperator !== DEFAULT_GROUP_OPERATOR) {
      const andRequest = {
        type: BooleanQueryType.AND,
        args: groupIds.map((id) => ({
          type: BooleanQueryType.GROUPS,
          groups: [id],
        })),
      };
      return this.groupIs ? andRequest : ({ type: BooleanQueryType.NOT, arg: andRequest } as BooleanQueryRequest);
    }

    const request: GroupsRequest = { type: BooleanQueryType.GROUPS, groups: groupIds };
    if (this.groupIs) {
      return request as BooleanQueryRequest;
    } else {
      return { type: BooleanQueryType.NOT, arg: request } as BooleanQueryRequest;
    }
  }

  private getMessage(labels: string[]): string {
    if (this.groupIs) {
      const operator =
        this.logicalOperator === 'OR'
          ? this.translocoService.translate('translations.reviews.filter.or')
          : this.translocoService.translate('translations.filter.and');
      return (
        this.translocoService.translate('translations.reviews.filter.group') +
        ' ' +
        this.translocoService.translate('translations.reviews.filter.is') +
        ' ' +
        labels.join(' ' + operator + ' ')
      );
    } else {
      return (
        this.translocoService.translate('translations.reviews.filter.group') +
        ' ' +
        this.translocoService.translate('translations.reviews.filter.isNot') +
        ' ' +
        labels.join(' ' + this.translocoService.translate('translations.reviews.filter.andNot') + ' ')
      );
    }
  }

  private getSettingsFromFilters(): void {
    if (this.selectedFilters) {
      const statusFilter = this.selectedFilters.find((f) => f.type === 'GROUP');
      if (statusFilter) {
        const booleanRequest = statusFilter.request as BooleanQueryRequest;
        const activeUserGroups = statusFilter.activeUserGroups;
        let groups: string[];

        if (booleanRequest.type === BooleanQueryType.AND) {
          this.logicalOperator = 'AND';
          groups = (booleanRequest as AndRequest).args
            .filter((arg) => arg.type === BooleanQueryType.GROUPS)
            .flatMap((arg) => (arg as GroupsRequest).groups);
        } else if (booleanRequest.type === BooleanQueryType.OR) {
          this.logicalOperator = 'OR';
          groups = (booleanRequest as OrRequest).args
            .filter((arg) => arg.type === BooleanQueryType.GROUPS)
            .flatMap((arg) => (arg as GroupsRequest).groups);
        } else if (booleanRequest.type === BooleanQueryType.NOT) {
          this.groupIs = false;
          const arg = (booleanRequest as NotRequest).arg;
          if (arg.type === BooleanQueryType.AND || arg.type === BooleanQueryType.OR) {
            this.logicalOperator = arg.type === BooleanQueryType.AND ? 'AND' : 'OR';
            groups = (arg as AndRequest).args
              .filter((inner) => inner.type === BooleanQueryType.GROUPS)
              .flatMap((inner) => (inner as GroupsRequest).groups);
          } else {
            groups = (arg as GroupsRequest).groups;
          }
        } else {
          this.groupIs = true;
          groups = (booleanRequest as GroupsRequest).groups;
        }

        if (activeUserGroups) {
          this.addActiveGroupsFromFilter(activeUserGroups, groups);
        } else {
          this.loadGroupsFromService(groups);
        }
      }
    }
  }

  private addActiveGroupsFromFilter(activeUserGroups: UserGroups[], groups: string[]): void {
    activeUserGroups.forEach((group) => {
      if (groups.includes(group._id)) {
        this.activeGroups.push(group);
      }
    });
    this.cdr.detectChanges();
  }

  private loadGroupsFromService(groups: string[]): void {
    const uids: (string | undefined)[] = this.playlistUids.length ? this.playlistUids : [undefined];
    groups.forEach((group) => {
      uids.forEach((uid) => {
        this.userGroupsService.loadUserGroup(group, uid).subscribe(({ isSuccess, value }) => {
          if (isSuccess) {
            this.activeGroups.push(value);
            this.cdr.detectChanges();
          }
        });
      });
    });
  }

  private fireSearch(): void {
    this.page = 0;
    this.loadingGroups = true;
    this.searchGroups((result) => {
      if (result.isSuccess) {
        const { value } = result;
        this.loadingGroups = false;
        this.foundGroups = value.content;
        this.canLoadMore = this.foundGroups.length < value.totalNumberOfElement;
        this.cdr.detectChanges();
      }
    });
  }

  private searchGroups(searchCallback: (result: Result<Page<UserGroups>, string, string[]>) => void): void {
    if (this.playlistUids.length > 0) {
      this.playlistUids.forEach((playlistUid) => {
        this.userGroupsService.searchUserGroups(this.page, this.DEFAULT_PAGE_SIZE, this.searchTerm, playlistUid).subscribe(searchCallback);
      });
    } else {
      this.userGroupsService.searchUserGroups(this.page, this.DEFAULT_PAGE_SIZE, this.searchTerm).subscribe(searchCallback);
    }
  }

  // With several playlists each request answers separately, so the same group can come back twice.
  private addFoundGroups(groups: UserGroups[]): void {
    const newGroups = groups.filter((group) => !this.foundGroups.some((found) => found._id === group._id));
    this.foundGroups = this.foundGroups.concat(newGroups);
  }
}
